import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, History, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import EditableField from '../../../components/ui/EditableField';
import { generateFieldConfig, RiskFieldConfig } from './fieldsConfig';
import { cleanDateAndTimeObject, renderDate, toLocalDateString } from '../../../../shared/renderDate';
import './riskdetail.css';
import { useTheme } from '@/contexts/ThemeContext';
import { colorValues } from '@/contexts/ThemeContext';
import { getAuthUID, isAuthLoaded, onAuthReady } from '@/lib/authState';
import { useTenancy } from '@/contexts/TenancyContext';
import { Risk } from '@/types/risks';
import { getRisk, putRisk, createRisk, getMenus, deleteRisk } from '@/services/risksService';
import { getAsset, updateAsset } from '@/services/assetsService';
import type { AssetRegister } from '@/js/external-js/variableModules/namespaces/AssetRegister.namespace';
import { toast } from 'sonner';
import { getAllRisks } from '../../../services/risksService';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faHistory } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { routePathByName } from '@/app/routes';
import { getCompanyInfo } from '@/services/companyService';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { RiskLinkedDocuments } from '@/components/risk/RiskLinkedDocuments';

interface RiskPageProps {
  isReadOnly?: boolean;
  onBackNavigation?: () => void;
}

type RiskEntity = Risk & { riskHistory?: any[] };

const RiskPage: React.FC<RiskPageProps> = ({ isReadOnly = false, onBackNavigation }) => {
  const [uid, setUid] = useState<string | null>(isAuthLoaded() ? getAuthUID() : null);
  const { activeTenancy, users, userIdToName } = useTenancy();
  const tenancyId = activeTenancy?.id || window.companyID;
  const [isCreateMode, setIsCreateMode] = useState(false);

  const { id: riskID } = useParams();
  const [riskItem, setRiskItem] = useState<Risk>();
  const [riskFieldsConfig, setRiskFieldsConfig] = useState<RiskFieldConfig[]>([]);
  const [riskHistory, setRiskHistory] = useState<Record<string, any>[]>([]);
  const [nextRiskId, setNextRiskId] = useState<string>("");
  const [changes, setChanges] = useState<Record<string, any>>();
  const [saving, setSaving] = useState(false);
  const activeFieldRef = useRef<string | null>(null);


  const [isEditing, setIsEditing] = useState(false);
  const [activeEditField, setActiveEditField] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true); // Set to true to show overlay
  const [notFound, setNotFound] = useState(false);

  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [activeTab, setActiveTab] = useState('details');
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [viewingHistoricalVersion, setViewingHistoricalVersion] = useState<boolean>(false);
  const [historicalRisk, setHistoricalRisk] = useState<RiskEntity | null>(null);
  const [isBannerSticky, setIsBannerSticky] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);


  const syncRiskAssetLinks = useCallback(async (riskId: string, previousAssetIds: string[], nextAssetIds: string[]) => {
    const companyId = tenancyId;
    if (!companyId || !riskId) return true;

    const normalize = (ids: string[]) => ids.filter(Boolean).map(id => id.toString());
    const prev = normalize(previousAssetIds);
    const next = normalize(nextAssetIds);

    const toAdd = next.filter(id => !prev.includes(id));
    const toRemove = prev.filter(id => !next.includes(id));

    if (!toAdd.length && !toRemove.length) return true;

    const tasks: Promise<void>[] = [];

    const updateAssetRisks = (assetId: string, action: 'add' | 'remove') => tasks.push((async () => {
      const assetInstance = await getAsset(assetId);
      const assetEntity = assetInstance?.entity as AssetRegister.AssetEntity | undefined;
      if (!assetEntity) return;

      const existingRisksSource = Array.isArray((assetEntity as any).assetRisks)
        ? (assetEntity as any).assetRisks
        : Array.isArray((assetEntity as any).risks)
          ? (assetEntity as any).risks
          : [];
      const existingRisks = existingRisksSource.filter(Boolean).map((id: any) => id.toString());

      let updatedRisks = existingRisks;
      if (action === 'add') {
        if (existingRisks.includes(riskId.toString())) return;
        updatedRisks = [...existingRisks, riskId.toString()];
      } else {
        if (!existingRisks.includes(riskId.toString())) return;
        updatedRisks = existingRisks.filter(id => id !== riskId.toString());
      }

      await updateAsset(assetId.toString(), {
        risks: updatedRisks,
      } as Partial<AssetRegister.AssetEntity>);
    })());

    toAdd.forEach(assetId => updateAssetRisks(assetId, 'add'));
    toRemove.forEach(assetId => updateAssetRisks(assetId, 'remove'));

    const results = await Promise.allSettled(tasks);
    const failed = results.filter(result => result.status === 'rejected');
    failed.forEach(result => console.error('[RiskDetail] Failed syncing risk-asset relationship', result));

    if (failed.length > 0) {
      throw new Error('One or more asset updates failed');
    }

    return true;
  }, [tenancyId]);


  // Set up scroll listener and measure banner height
  useEffect(() => {
    if (!bannerRef.current) return;

    // Measure banner height
    setBannerHeight(bannerRef.current.offsetHeight);

    const handleScroll = () => {
      if (!bannerRef.current) return;

      const scrollPosition = window.scrollY;
      const bannerPosition = bannerRef.current.offsetTop;
      const bannerHeight = bannerRef.current.offsetHeight;

      // Make banner sticky when scrolled past its original position
      setIsBannerSticky(scrollPosition > bannerPosition);
    };

    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    // Initial check
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [viewingHistoricalVersion]);

  // Handle version selection - shows the state of the risk before the selected change was made
  const handleVersionSelect = (index: number) => {
    console.log('handleVersionSelect called with index:', index);
    console.log('Current riskHistory:', riskHistory);

    setSelectedVersion(index);
    if (index !== null) {
      // Start with the current risk state (deep copy)
      const versionedRisk = JSON.parse(JSON.stringify(riskItem));
      
      // Sort history by date (most recent first)
      const sortedHistory = [...riskHistory].sort((a, b) => 
        (cleanDateAndTimeObject(b.dateChanged)?.getTime() || 0) - 
        (cleanDateAndTimeObject(a.dateChanged)?.getTime() || 0)
      );
      
      console.log('Sorted history:', sortedHistory);
      
      // Get the selected change
      const selectedChange = sortedHistory[index];
      console.log('Selected change:', selectedChange);
      
      // If this is the first change, we need to handle it specially
      if (index === sortedHistory.length - 1) {
        // For the oldest change, we only need to apply the old values from this change
        const changes = selectedChange.riskNew || selectedChange.changes || {};
        Object.entries(changes).forEach(([key, changeValue]) => {
          // Handle both old and new format
          if (selectedChange.changes) {
            const change = changeValue as { elementName: string; oldValue: any; newValue: any };
            console.log(`  Reverting ${key} to old value:`, change.oldValue);
            // Special handling for riskRating to ensure it's properly set
            if (key === 'riskRating' && change.oldValue !== undefined) {
              versionedRisk[key] = change.oldValue;
            } else {
              versionedRisk[key] = change.oldValue;
            }
          } else {
            // Old format - just take the value as is
            versionedRisk[key] = changeValue;
          }
        });
        
        console.log('Final versionedRisk state:', versionedRisk);
        setHistoricalRisk(versionedRisk);
        setViewingHistoricalVersion(true);
        return;
      }
      
      // For non-first changes, get all changes up to but not including the selected one
      const changesToApply: Record<string, any> = {};
      
      // Process changes in chronological order (oldest first) up to but not including the selected change
      sortedHistory.slice(index + 1).reverse().forEach(historyItem => {
        const changes = historyItem.riskNew || historyItem.changes || {};
        
        Object.entries(changes).forEach(([key, changeValue]) => {
          if (historyItem.changes) {
            // New format with oldValue/newValue
            const change = changeValue as { elementName: string; oldValue: any; newValue: any };
            // Only apply if we haven't seen this field before (we're going from old to new)
            if (changesToApply[key] === undefined) {
              changesToApply[key] = change.oldValue;
            }
          } else {
            // Old format - just take the value as is
            changesToApply[key] = changeValue;
          }
        });
      });
      
      // Now apply all the changes to reconstruct the historical state
      console.log('Changes to apply to reconstruct historical state:', changesToApply);
      
      // First, apply all the changes from history up to the selected point
      Object.entries(changesToApply).forEach(([key, value]) => {
        console.log(`  Setting ${key} to:`, value);
        // Handle nested objects by merging them
        if (value && typeof value === 'object' && !Array.isArray(value) && versionedRisk[key] && typeof versionedRisk[key] === 'object') {
          versionedRisk[key] = { ...versionedRisk[key], ...value };
        } else {
          versionedRisk[key] = value;
        }
      });
      
      // Then, apply the old values from the selected change to ensure we show the state BEFORE this change
      const selectedChanges = selectedChange.riskNew || selectedChange.changes || {};
      Object.entries(selectedChanges).forEach(([key, changeValue]) => {
        if (selectedChange.changes) {
          const change = changeValue as { elementName: string; oldValue: any; newValue: any };
          if (change.oldValue !== undefined) {
            console.log(`  Reverting ${key} to old value from selected change:`, change.oldValue);
            versionedRisk[key] = change.oldValue;
          }
        }
      });

      console.log('Final versionedRisk state:', versionedRisk);
      setHistoricalRisk(versionedRisk);
      setViewingHistoricalVersion(true);
      console.log('Historical view enabled for version', index);
    } else {
      console.log('Exiting historical view');
      setViewingHistoricalVersion(false);
      setHistoricalRisk(null);
    }
  };

  const exitHistoricalView = () => {
    setViewingHistoricalVersion(false);
    setHistoricalRisk(null);
    setSelectedVersion(null);
  };

  const controlIds = React.useMemo(() => {
    const sel = Array.isArray((riskItem as any)?.selectedControls) ? (riskItem as any).selectedControls : [];
    const rel = Array.isArray((riskItem as any)?.relatedControls) ? (riskItem as any).relatedControls : [];
    return Array.from(new Set([...sel, ...rel].filter(Boolean).map((x: any) => String(x))));
  }, [JSON.stringify((riskItem as any)?.selectedControls || []), JSON.stringify((riskItem as any)?.relatedControls || [])]);

  // Default entity link for this Risk
  const defaultEntityLink = React.useMemo(() => {
    const ref = String((riskItem?.riskID ?? riskItem?.id) ?? '');
    const title = String(riskItem?.risk || riskItem?.description || 'Current Risk');
    if (!ref) return undefined as any;
    return {
      id: `internal:risk:${ref}`,
      ref,
      title,
      domain: 'Risk',
      relation: 'mitigates',
    };
  }, [riskItem?.riskID, riskItem?.id, riskItem?.risk, riskItem?.description]);

  // Mappings card now handles its own discovery and loading; no local effect needed

  // Determine which risk to display - current or historical version
  const displayRisk = viewingHistoricalVersion && historicalRisk ? historicalRisk : riskItem;

  const handleOnSave = async (fieldKey: string, value) => {
    console.log('[Risk Detail] onSave', fieldKey, value);

    // Record the active field being edited
    activeFieldRef.current = fieldKey;

    // For create mode, just update state without API call
    if (isCreateMode) {
      setRiskItem(prevState => {
        return {
          ...prevState,
          [fieldKey]: value
        } as Risk;
      });

      // Also track changes in create mode
      setChanges(prev => ({
        ...(prev || {}),
        [fieldKey]: value
      }));

      toast.success(`Updated ${fieldKey}`);
      return;
    }

    // For existing risks, save to backend
    toast.loading(`Saving ${fieldKey}...`);

    try {
      // Create a minimal Risk object with only the required ID and the field being updated
      const riskUpdate = {
        ...riskItem,  // Include all existing properties to maintain type integrity
        [fieldKey]: value, // Update the specific field
      };

      // Remove riskHistory because the backend handles it
      const riskUpdateCopy = { ...riskUpdate };
      delete riskUpdateCopy.riskHistory;

      await putRisk(tenancyId, riskItem.riskID, riskUpdateCopy as Risk);

      if (fieldKey === 'assets') {
        const previousAssets = Array.isArray(riskItem?.assets)
          ? riskItem.assets.map(assetId => assetId?.toString?.() ?? '')
          : [];
        const nextAssets = Array.isArray(value)
          ? value.map(assetId => assetId?.toString?.() ?? '')
          : typeof value === 'string'
            ? value.split(',').map(assetId => assetId.trim()).filter(Boolean)
            : [];

        try {
          // DED-1261: Use risk.id (componentId || riskID) for consistency with RisksCombobox
          const riskIdForSync = (riskItem as any)?.componentId?.toString?.() ?? riskItem?.riskID?.toString?.() ?? riskID ?? '';
          await syncRiskAssetLinks(riskIdForSync, previousAssets, nextAssets);
          toast.success('Risk-asset relationships synced successfully');
        } catch (syncError) {
          console.error('[Risk Detail] Failed to sync risk-asset relationships', syncError);
          toast.error('Failed to sync risk-asset relationships');
        }
      }

      setRiskItem(prevState => {
        // Create a properly typed Risk object with the updated field
        const updatedRisk = {
          ...prevState,
          [fieldKey]: value,
        };
        return updatedRisk as Risk;
      });

      // Track changes for edit mode too
      setChanges(prev => ({
        ...(prev || {}),
        [fieldKey]: value
      }));

      toast.dismiss();
      toast.success(`Saved ${fieldKey}`);

      // Clear active field
      activeFieldRef.current = null;
    } catch (error) {
      toast.dismiss();
      toast.error(`Failed to save ${fieldKey}`);
      console.error(error);

      // Clear active field
      activeFieldRef.current = null;
    }
  };

  // Helper to add MCM control to risk's relatedControls
  const addMcmControlToRisk = useCallback(async (controlId: string) => {
    if (!riskItem || !tenancyId || isCreateMode) return;
    
    const currentRelated = Array.isArray((riskItem as any)?.selectedControls) 
      ? (riskItem as any).selectedControls 
      : [];
    
    // Check if already exists
    if (currentRelated.includes(controlId)) {
      console.log(`[RiskDetail] Control ${controlId} already in selectedControls`);
      return;
    }
    
    // Add to array
    const updatedRelated = [...currentRelated, controlId];
    
    console.log(`[RiskDetail] Adding ${controlId} to selectedControls`);
    
    try {
      // Direct Firestore update without triggering full state refresh
      const riskUpdate = {
        ...riskItem,
        selectedControls: updatedRelated,
      };
      const riskUpdateCopy = { ...riskUpdate } as any;
      delete riskUpdateCopy.riskHistory;
      
      await putRisk(tenancyId, riskItem.riskID, riskUpdateCopy as Risk);
      
      // Update local state only
      setRiskItem(prevState => ({
        ...prevState,
        selectedControls: updatedRelated,
      } as Risk));
      
      console.log(`[RiskDetail] Added ${controlId} to selectedControls`);
    } catch (e) {
      console.warn('[RiskDetail] Failed to add control to selectedControls', e);
    }
  }, [riskItem, tenancyId, isCreateMode]);

  useEffect(() => {
    if (!isAuthLoaded()) {
      const unsub = onAuthReady(() => {
        setUid(getAuthUID());
      });

      return unsub;
    }
  }, []);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteRisk(tenancyId, (riskItem.riskID || riskItem.id));
      navigate(routePathByName('risk-register'));
      toast.success('Risk deleted successfully');
    } catch (error) {
      toast.error('Failed to delete risk');
      console.error(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  useEffect(() => {
    if (!tenancyId) {
      console.log('no tenant id');
      return;
    }

    const loadRisk = async () => {
      try {
        // Check if we're creating a new risk
        if (riskID === 'new') {
          setIsCreateMode(true);
          setIsEditing(true);

          // Get menus for field configuration
          const menus = await getMenus();

          // Set up default risk values
          const today = new Date();
          const nextReviewDate = new Date(today);
          nextReviewDate.setFullYear(today.getFullYear() + 1);

          // Fetch current risks to generate next risk ID
          try {
            // We need to fetch existing risks to calculate the next risk ID
            const currentRisks = await getAllRisks(tenancyId);

            // Calculate next risk ID (similar to risk-register implementation)
            const existingRiskIds = new Set(currentRisks.map(risk => risk.riskID));
            let maxId = 0;

            currentRisks.forEach(risk => {
              const idNum = parseInt(risk.riskID || "0", 10);
              if (!isNaN(idNum) && idNum > maxId) {
                maxId = idNum;
              }
            });

            // Increment to get the next ID
            const nextId = (maxId + 1).toString();
            setNextRiskId(nextId);

            console.log(`Generated next risk ID: ${nextId}`);

            // Create default risk with the generated ID
            // Using the format from valid risk objects
            const defaultRisk = {
              id: nextId,
              riskID: parseInt(nextId, 10),
              riskIDstatus: "active",
              risk: '',
              businessUnit: menus?.businessUnit?.[0] || "",
              cause: "",
              componentId: `RSK-${nextId}`,
              controlEffect: menus?.controlEffect?.[0] || "No credible control - 1",
              controls: "",
              dateAdded: new Date(),
              impact: "",
              inherentRisk: "Low",
              lastReviewDate: new Date(),
              nextReviewDate: nextReviewDate,
              residualRisk: "Low",
              response: "",
              riskOwner: "",
              selectedControls: [],
              targetRisk: "Low",
              treatRequired: "",
              treatmentPlan: "",
              version: "1.0",
              status: 'Active',
              type: 'Other',
              riskCategory: '',
              criticality: 'Medium',
              dataClassification: 'Business Confidential',
              subprocessor: false,
              publishInPrivacyPolicy: false,
              annualSecurityReviewCompleted: false,
              vendorManagementNeeded: false,
              dataTypesShared: [],
              dataStorageLocations: [],
              portable: false,
              serialNumber: '',
              physicalLabel: false,
              socCriticality: 'N/A',
              backupsRequired: false,
              riskRating: 'Medium',
              treatmentRequired: 'No',
              history: [{
                changedBy: uid || "Unknown",
                dateChanged: new Date(),
                riskNew: {
                  status: "Created via form"
                }
              }],
              riskHistory: [],
              assets: [],
              menus: menus || {}
            } as unknown as Risk;

            setRiskItem(defaultRisk);
            const fields = generateFieldConfig(menus, users || [], true); // Pass isCreateMode=true
            setRiskFieldsConfig(fields);
            setLoading(false);
          } catch (error) {
            console.error("Error calculating next risk ID:", error);

            // Create default risk without ID if there was an error
            const defaultRisk = {
              id: "",
              riskID: 0,
              riskIDstatus: "active",
              risk: '',
              businessUnit: menus?.businessUnit?.[0] || "",
              cause: "",
              componentId: "",
              controlEffect: menus?.controlEffect?.[0] || "No credible control - 1",
              controls: "",
              dateAdded: new Date(),
              impact: "",
              inherentRisk: "Low",
              lastReviewDate: new Date(),
              nextReviewDate: nextReviewDate,
              residualRisk: "Low",
              response: "",
              riskOwner: "",
              selectedControls: [],
              targetRisk: "Low",
              treatRequired: "",
              treatmentPlan: "",
              version: "1.0",
              status: 'Active',
              type: 'Other',
              riskCategory: '',
              criticality: 'Medium',
              dataClassification: 'Business Confidential',
              subprocessor: false,
              publishInPrivacyPolicy: false,
              annualSecurityReviewCompleted: false,
              vendorManagementNeeded: false,
              dataTypesShared: [],
              dataStorageLocations: [],
              portable: false,
              serialNumber: '',
              physicalLabel: false,
              socCriticality: 'N/A',
              backupsRequired: false,
              riskRating: 'Medium',
              treatmentRequired: 'No',
              history: [{
                changedBy: uid || "Unknown",
                dateChanged: new Date().toISOString(),
                riskNew: {
                  status: "Created via form"
                }
              }],
              riskHistory: [],
              menus: menus || {}
            };

            setRiskItem(defaultRisk);
            const fields = generateFieldConfig(menus, users || [], true);
            setRiskFieldsConfig(fields);
            setLoading(false);
          }
          return;
        }

        // Load existing risk
        // const risk = await getRisk(tenancyId, riskID);
        // console.log(`Getting risk: ${riskID}:`, risk.menus, users, uid);
        // setIsCreateMode(false);
        // setRiskItem(risk.entry);

        // const fields = generateFieldConfig(risk.menus, users, false); // Pass isCreateMode=false
        // setRiskFieldsConfig(fields);
        // setLoading(false);
        // Load existing risk
        let risk: Risk | null = null;
        try {
          console.log(`[Risk Detail] Getting risk: ${riskID}:`, tenancyId, riskID)
          risk = await getRisk(tenancyId, riskID);
          setIsCreateMode(false);
        } catch (error) {
          console.error('Error loading risk:', error, error.code, error.status, error.message);
          toast.error('Failed to load risk or not found');
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (!risk) {
          console.error('Error loading risk: risk is null/undefined');
          toast.error('Failed to load risk or not found');
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Handle both formats: direct risk object or { entry: risk, menus: ... }
        const riskData = (risk as any).entry ?? risk;
        const menus = (risk as any).menus;

        console.log(`[Risk Detail] Got risk: ${riskID}:`, menus, users, uid);
        console.log('[Risk Detail] Got risk:', risk);
        setIsCreateMode(false);
        setRiskItem(({
          ...riskData,
          assets: Array.isArray(riskData.assets)
            ? riskData.assets
            : Array.isArray(riskData.relatedAssets)
              ? riskData.relatedAssets
              : [],
        } as unknown) as Risk);
        if (riskData.riskHistory && riskData.riskHistory.length > 0) {
          const sortedHistory = riskData.riskHistory.sort((a: any, b: any) => (cleanDateAndTimeObject(b.dateChanged)?.getTime() || 0) - (cleanDateAndTimeObject(a.dateChanged)?.getTime() || 0));
          setRiskHistory(sortedHistory);
        } else {
          setRiskHistory([]);
        }

        // Get business units from company settings (customerOrg.teams.businessUnitNames)
        const companyInfo = await getCompanyInfo(tenancyId);
        const businessUnitNames = (companyInfo?.customerOrg?.teams?.businessUnitNames || {}) as Record<string, { name: string }>;
        const companyBusinessUnits = Object.values(businessUnitNames).map(bu => bu.name);
        console.log('[Risk Detail] Got businessUnits from company settings:', companyBusinessUnits);

        // Combine with existing risk.menus.businessUnit and de-duplicate
        const existingBusinessUnits = risk.menus?.businessUnit || [];
        const deduplicatedBusinessUnits = [...new Set([...existingBusinessUnits, ...companyBusinessUnits])];
        console.log('[Risk Detail] Deduplicated businessUnits:', deduplicatedBusinessUnits);

        risk.menus.businessUnit = deduplicatedBusinessUnits;
        console.log('[Risk Detail] Updated menus:', risk.menus);

        const fields = generateFieldConfig(risk.menus, users, false); // Pass isCreateMode=false
        setRiskFieldsConfig(fields);
        setLoading(false);
      } catch (error) {
        console.error('Error loading risk:', error);
        toast.error('Failed to load risk');
        setLoading(false);
      }
    };

    loadRisk();
  }, [tenancyId, riskID]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-overlay">
          <div className="spinner" />
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  // Show friendly error page if risk not found
  if (notFound) {
    return (
      <div className="page-container">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '6rem',
            marginBottom: '1rem'
          }}>
            🦆
          </div>
          <h1 style={{
            fontSize: '2rem',
            marginBottom: '1rem',
            color: theme.isDark ? '#f3f4f6' : '#18181b'
          }}>
            Quack! Risk Not Found
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '2rem',
            maxWidth: '600px',
            color: theme.isDark ? '#d1d5db' : '#4b5563'
          }}>
            Sorry, we couldn't find the risk you're looking for. It may have been deleted or never existed.
          </p>
          {!isReadOnly && (
            <button
              className="primary-btn"
              style={{
                background: colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary,
                color: theme.isDark ? '#fff' : '#18181b',
                border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`,
                padding: '0.75rem 2rem',
                fontSize: '1.1rem',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onClick={() => onBackNavigation ? onBackNavigation() : navigate('/risk-register')}
            >
              <i className="fa-solid fa-chevron-left"></i> {onBackNavigation ? 'Back to Auditor Risk Register' : 'Back to Risk Register'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Historical Version Banner - Positioned under navigation */}
      {viewingHistoricalVersion && historicalRisk && selectedVersion !== null && (
        <div
          className={`w-full bg-yellow-50 dark:bg-yellow-900/90 border-b border-yellow-200 dark:border-yellow-800 py-2 transition-all duration-200 justify-center ${isBannerSticky ? 'fixed top-0 left-0 right-0 z-40 shadow-md' : 'hidden'
            }`}
          style={{
            backdropFilter: 'blur(4px)',
            marginTop: isBannerSticky ? '0' : '0',
          }}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faHistory} className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                  Viewing historical version from {renderDate(riskHistory[selectedVersion]?.dateChanged, { dateStyle: 'long' })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleVersionSelect(null)}
                className="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded px-2 py-1"
              >
                Return to current version
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add padding to the body when banner is sticky */}
      {isBannerSticky && viewingHistoricalVersion && (
        <div className="h-10 w-full"></div>
      )}
      <section className="container" style={{
        minHeight: '100%',
        minWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          width: '100%',
          maxWidth: 1100,
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '1rem'
        }}>
          {!isReadOnly && (
            <button className="back-button" onClick={() => navigate('/risk-register')}>
              <i className="fa-solid fa-chevron-left"></i> Back to Risk Register
            </button>
          )}
        </div>
        <div
          className="glass-card"
          style={{
            width: '100%',
            maxWidth: 1100,
            backgroundColor: theme.isDark ? '#18181b' : 'white',
            color: theme.isDark ? '#f3f4f6' : '#18181b',
            border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
            boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <div className="header-row flex flex-row justify-between">
            <div>
              <h2 className="title">{riskItem?.risk || "Untitled Risk"}</h2>
              <div className="subtitle">{riskItem?.category || ""}</div>
            </div>
            <div className="header-actions">
              {!isCreateMode ? (
                <div className="secondary-actions flex flex-row gap-2">
                  {!isReadOnly && <div><Button variant='destructive' onClick={() => setShowDeleteDialog(true)}>Delete</Button></div>}
                  <Button variant='secondary' onClick={() => setShowHistory(!showHistory)}>
                    <FontAwesomeIcon icon={showHistory ? faEyeSlash : faHistory} />
                    <span>{showHistory ? 'Hide History' : 'History'}</span>
                  </Button>
                </div>
              ) : (
                <button
                  className="primary-btn"
                  style={{
                    background: colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary,
                    color: theme.isDark ? '#fff' : '#18181b',
                    border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`
                  }}
                  onClick={async () => {
                    try {
                      setSaving(true);
                      toast.loading('Creating new risk...');

                      // Reset active field
                      activeFieldRef.current = null;

                      if (!riskItem) {
                        toast.error('No risk data to save');
                        setSaving(false);
                        return;
                      }

                      // Since our EditableField components now update state properly,
                      // we can just use the current riskItem directly
                      console.log('Creating risk with current state data:', riskItem);

                      // Remove any non-backend properties
                      const { menus, ...riskDataToSave } = riskItem;

                      // Save the new risk with auto-generated risk ID
                      const result = await createRisk(tenancyId, nextRiskId, riskDataToSave);

                      try {
                        const createdRiskId = (result?.riskID ?? result?.id ?? nextRiskId)?.toString?.() ?? nextRiskId;
                        const selectedAssets = Array.isArray(riskItem.assets)
                          ? riskItem.assets.map(assetId => assetId?.toString?.() ?? '')
                          : [];
                        await syncRiskAssetLinks(createdRiskId, [], selectedAssets);
                        toast.success('Risk-asset relationships synced successfully');
                      } catch (syncError) {
                        console.error('[Risk Detail] Failed to sync risk-asset relationships on create', syncError);
                        toast.error('Failed to sync risk-asset relationships');
                      }

                      toast.dismiss();
                      toast.success('Risk created successfully');

                      // Reset state and navigate to the newly created risk
                      setRiskItem(({
                        ...result,
                        assets: Array.isArray((result as any)?.assets)
                          ? (result as any).assets
                          : Array.isArray(riskItem.assets)
                            ? riskItem.assets
                            : [],
                        menus: (result as any)?.menus ?? riskItem.menus ?? {},
                      } as unknown) as Risk);
                      setIsCreateMode(false);
                      setIsEditing(false);
                      setSaving(false);
                      setChanges(undefined);
                      navigate(`/riskdetail/${result.id}`);
                    } catch (error) {
                      toast.dismiss();
                      toast.error('Failed to create risk');
                      console.error('Error creating risk:', error);
                      setSaving(false);
                    }
                  }}
                >
                  <i className="fa-solid fa-save"></i> Create Risk
                </button>
              )}
            </div>
          </div>
          {/* Dynamically render all sections and fields */}
          {Array.from(new Set(riskFieldsConfig.map(f => f.section))).map(section => (
            <div className="risk-section" key={section}>
              <h3 className="section-title">{section}</h3>
              <div className="property-grid">
                {riskFieldsConfig.filter(f => f.section === section).map(field => (
                  <div
                    key={field.key}
                    className={
                      field.fullWidth
                        ? 'property-item full-width'
                        : 'property-item'
                    }
                  >
                    <EditableField
                      isCreateMode={isCreateMode}
                      label={field.label}
                      value={displayRisk?.[field.key]}
                      type={field.type}
                      options={field.options}
                      readOnly={field.readOnly || viewingHistoricalVersion || isReadOnly}
                      // For create mode, always set editing=true for all fields
                      // For normal mode, only set editing for the active field
                      editing={!isReadOnly && !viewingHistoricalVersion && (isCreateMode || (activeEditField === field.key))}
                      onSave={value => {
                        if (viewingHistoricalVersion) return; // Don't allow saving in historical view
                        
                        if (isCreateMode) {
                          // Just update local state for create mode without API calls
                          setRiskItem(prev => ({
                            ...prev,
                            [field.key]: value
                          }));

                          // Track changes for final creation
                          setChanges(prev => ({
                            ...(prev || {}),
                            [field.key]: value
                          }));

                          console.log(`Direct field update: ${field.key} = ${JSON.stringify(value)}`);
                        } else {
                          // Normal field save with API call for edit mode
                          handleOnSave(field.key, value);
                          // Clear the active edit field after save
                          setActiveEditField(null);
                        }
                      }}
                      // In create mode or historical view, hide the individual save button
                      hideActionButton={isCreateMode || viewingHistoricalVersion}
                      // For edit mode, add field-specific edit control
                      {... (!isCreateMode && !viewingHistoricalVersion && !isReadOnly ? {
                        setEditing: (editing) => {
                          if (editing) {
                            setActiveEditField(field.key);
                          } else {
                            setActiveEditField(null);
                          }
                        }
                      } : {})}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* History Panel (toggleable) - Legacy panel, can be removed if modal is preferred */}
          {/* {showHistory && (
            <div className="glass-card" id="historyPanel" style={{ marginTop: 24 }}>
              <h3 className="section-title">Change History</h3>
              <div className="property-grid">
                <div className="property-item">
                  <button
                    className="primary-btn"
                    style={{
                      background: colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary,
                      color: theme.isDark ? '#fff' : '#18181b',
                      border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`
                    }}
                    onClick={() => setShowHistoryModal(true)}
                  >
                    View Detailed History
                  </button>
                </div>
              </div>
            </div>
          )} */}

          {/* Risk History Modal */}
          {showHistory && (
            <div className="bg-white/85 dark:bg-neutral-900/80 backdrop-blur-md rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-6 mb-6 overflow-hidden"
              id="historyPanel"
              style={{ marginTop: 24 }}
              ref={historyPanelRef}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Version History</h3>
                <div className="flex gap-2">
                  <Tabs>
                    <TabsList className="h-8">
                      <TabsTrigger
                        value="all"
                        className="text-xs px-3 py-1 h-7"
                        onClick={() => setActiveTab('all')}
                      >
                        All Changes
                      </TabsTrigger>
                      {!isReadOnly && (
                        <TabsTrigger
                          value="restore"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => setActiveTab('restore')}
                        >
                          Restore Version
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              <div className="flex flex-col gap-4 max-h-[500px] overflow-y-auto pr-2 -mr-2">
                {riskHistory.length > 0 ? (
                  riskHistory.filter(item => {
                    // Filter out history items with no actual changes
                    const changes = Object.entries(item.riskNew || item.changes || {});
                    return changes.some(([_, changeValue]) => {
                      const change = changeValue as {
                        elementName: string;
                        oldValue: any;
                        newValue: any;
                      };
                      const oldVal = item.changes ? change.oldValue : (changeValue as any);
                      const newVal = item.changes ? change.newValue : (changeValue as any);
                      return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                    });
                  }).map((item, index) => {
                    const formattedDate = renderDate(item.dateChanged, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    });
                    const isCurrent = index === 0 && selectedVersion === null;
                    const isSelected = selectedVersion === index;

                    return (
                      <Card
                        key={index}
                        className={`relative overflow-visible transition-all ${isSelected ? 'ring-2 ring-primary' : ''} ${isCurrent ? 'border-l-4 border-l-green-500' : ''
                          }`}
                      >
                        <CardHeader className="p-4 pb-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-sm font-medium flex items-center gap-2">
                                {isCurrent ? (
                                  <Badge variant="outline" className="text-xs">Current</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">
                                    Version {riskHistory.length - index}
                                  </Badge>
                                )}
                                {userIdToName[item.changedBy]}
                              </CardTitle>
                              <CardDescription className="text-xs mt-1">
                                {formattedDate}
                              </CardDescription>
                            </div>
                            {!isCurrent && activeTab === 'restore' && !isReadOnly && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreClick(item, index)}
                                disabled={isRestoring}
                                className="text-xs h-7"
                              >
                                {isRestoring && selectedVersion === index ? (
                                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-3 h-3 mr-1.5" />
                                )}
                                Restore
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="p-4 pt-0 overflow-visible">
                          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                            {(() => {
                              const changeCount = Object.entries(item.riskNew || item.changes || {}).filter(([_, changeValue]) => {
                                const change = changeValue as {
                                  elementName: string;
                                  oldValue: any;
                                  newValue: any;
                                };
                                const oldVal = item.changes ? change.oldValue : (changeValue as any);
                                const newVal = item.changes ? change.newValue : (changeValue as any);
                                return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                              }).length;
                              
                              // Only show the count if there are actual changes
                              return changeCount > 0 ? `${changeCount} ${changeCount === 1 ? 'change' : 'changes'} made` : 'No changes';
                            })()}
                          </div>

                          <div className="space-y-3">
                            {Object.entries(item.riskNew || item.changes || {})
                              .filter(([_, changeValue]) => {
                                const change = changeValue as {
                                  elementName: string;
                                  oldValue: any;
                                  newValue: any;
                                };
                                
                                // Only show if there's an actual difference between old and new values
                                const oldVal = item.changes ? change.oldValue : (changeValue as any);
                                const newVal = item.changes ? change.newValue : (changeValue as any);
                                
                                // Handle different value types for comparison
                                const areEqual = (a: any, b: any): boolean => {
                                  // Handle null/undefined cases
                                  if (a === b) return true;
                                  if (a == null || b == null) return false;
                                  
                                  // Handle arrays
                                  if (Array.isArray(a) && Array.isArray(b)) {
                                    if (a.length !== b.length) return false;
                                    return a.every((val, i) => areEqual(val, b[i]));
                                  }
                                  
                                  // Handle objects
                                  if (typeof a === 'object' && typeof b === 'object') {
                                    const keysA = Object.keys(a);
                                    const keysB = Object.keys(b);
                                    
                                    if (keysA.length !== keysB.length) return false;
                                    return keysA.every(key => areEqual(a[key], b[key]));
                                  }
                                  
                                  // Handle dates
                                  if (a instanceof Date && b instanceof Date) {
                                    return a.getTime() === b.getTime();
                                  }
                                  
                                  return false;
                                };
                                
                                return !areEqual(oldVal, newVal);
                              })
                              .slice(0, 3)
                              .map(([field, changeValue], i) => {
                                const change = changeValue as {
                                  elementName: string;
                                  oldValue: any;
                                  newValue: any;
                                };

                                const renderField = (key: string, value: any) => {
                                  if (value === null || value === undefined) return 'None';
                                  
                                  // Handle date fields
                                  if (key === 'dateAdded' || key === 'nextReviewDate' || key === 'lastReviewDate' || key === 'dateChanged') {
                                    try {
                                      return toLocalDateString(new Date(value));
                                    } catch (e) {
                                      console.warn(`Error formatting date for ${key}:`, value, e);
                                      return String(value);
                                    }
                                  }
                                  
                                  // Handle risk rating specifically
                                  if (key === 'riskRating') {
                                    return value || 'Not rated';
                                  }
                                  
                                  // Handle primitive types
                                  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                                    return String(value) || 'None';
                                  }
                                  
                                  // Handle arrays
                                  if (Array.isArray(value)) {
                                    return value.length > 0 ? value.join(', ') : 'None';
                                  }
                                  
                                  // Handle dates
                                  if (value instanceof Date || (value && value.toDate)) {
                                    try {
                                      const date = value.toDate ? value.toDate() : value;
                                      return toLocalDateString(date);
                                    } catch (e) {
                                      console.warn('Error formatting date:', value, e);
                                      return String(value);
                                    }
                                  }
                                  
                                  // Handle objects
                                  if (typeof value === 'object' && value !== null) {
                                    // If it's a Firebase Timestamp or similar
                                    if (value.seconds && typeof value.seconds === 'number') {
                                      try {
                                        return toLocalDateString(new Date(value.seconds * 1000));
                                      } catch (e) {
                                        console.warn('Error formatting timestamp:', value, e);
                                      }
                                    }
                                    return JSON.stringify(value, null, 2);
                                  }
                                  
                                  return String(value || 'None');
                                };

                                const fieldName = item.changes ? change.elementName : field;
                                const oldValue = item.changes ? change.oldValue : (changeValue as any);
                                const newValue = item.changes ? change.newValue : (changeValue as any);

                                return (
                                  <div key={i} className="text-xs">
                                    <div className="font-medium text-gray-700 dark:text-gray-200">
                                      {fieldName}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mt-1">
                                      <div className="text-gray-500 dark:text-gray-400 line-through">
                                        {renderField(fieldName, oldValue)}
                                      </div>
                                      <div className="text-primary font-medium">
                                        {renderField(fieldName, newValue)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            {Object.entries(item.riskNew || item.changes || {}).filter(([_, changeValue]) => {
                              const change = changeValue as {
                                elementName: string;
                                oldValue: any;
                                newValue: any;
                              };
                              const oldVal = item.changes ? change.oldValue : (changeValue as any);
                              const newVal = item.changes ? change.newValue : (changeValue as any);
                              return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                            }).length > 3 && (
                              <div className="text-xs text-primary/80 dark:text-primary/60">
                                {Object.entries(item.riskNew || item.changes || {}).filter(([_, changeValue]) => {
                                  const change = changeValue as {
                                    elementName: string;
                                    oldValue: any;
                                    newValue: any;
                                  };
                                  const oldVal = item.changes ? change.oldValue : (changeValue as any);
                                  const newVal = item.changes ? change.newValue : (changeValue as any);
                                  return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                                }).length - 3} more changes
                              </div>
                            )}
                          </div>

                          {activeTab !== 'restore' && (
                            <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                                <Button
                                  variant={selectedVersion === index ? "secondary" : "ghost"}
                                  size="sm"
                                  className="text-xs h-7 px-2"
                                  onClick={() => {
                                    if (selectedVersion === index) {
                                      // Clicking the same version button again toggles back to current view
                                      handleVersionSelect(null);
                                    } else {
                                      // Select this historical version
                                      handleVersionSelect(index);
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon 
                                    icon={faHistory} 
                                    className={`w-3 h-3 mr-1.5 ${selectedVersion === index ? 'text-primary' : ''}`} 
                                  />
                                  {selectedVersion === index 
                                    ? 'Back to Current Version' 
                                    : 'View Risk Before This Change'}
                                </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <FontAwesomeIcon icon={faHistory} className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">No version history with changes available</p>
                    <p className="text-sm text-gray-400 mt-1">Changes will appear here when you update this risk</p>
                  </div>
                )}
              </div>

              {(() => {
                const filteredHistory = riskHistory.filter(item => {
                  const changes = Object.entries(item.riskNew || item.changes || {});
                  return changes.some(([_, changeValue]) => {
                    const change = changeValue as {
                      elementName: string;
                      oldValue: any;
                      newValue: any;
                    };
                    const oldVal = item.changes ? change.oldValue : (changeValue as any);
                    const newVal = item.changes ? change.newValue : (changeValue as any);
                    return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                  });
                });
                
                return filteredHistory.length > 0 ? (
                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
                    <p>Showing {filteredHistory.length} version{filteredHistory.length !== 1 ? 's' : ''} with changes</p>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
        {/* DED-1507: Linked Documents section - moved above MCM per Ahmed's feedback */}
        {!isCreateMode && riskItem && (
          <div style={{ width: '100%', maxWidth: 1100 }}>
            <RiskLinkedDocuments
              riskId={riskItem.riskID?.toString() || riskItem.id?.toString() || ''}
              riskNextReviewDate={riskItem.nextReviewDate}
              riskControlIds={controlIds}
              linkedDocuments={Array.isArray((riskItem as any).linkedDocuments) ? (riskItem as any).linkedDocuments : []}
              onDocumentsChange={async (docIds) => {
                if (!tenancyId || !riskItem) return;
                try {
                  const riskUpdate = {
                    ...riskItem,
                    linkedDocuments: docIds,
                  };
                  const riskUpdateCopy = { ...riskUpdate } as any;
                  delete riskUpdateCopy.riskHistory;
                  await putRisk(tenancyId, riskItem.riskID, riskUpdateCopy as Risk);
                  setRiskItem(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      linkedDocuments: docIds,
                    } as RiskEntity;
                  });
                  toast.success('Linked documents updated');
                } catch (error) {
                  console.error('Failed to update linked documents:', error);
                  toast.error('Failed to update linked documents');
                }
              }}
              isReadOnly={isReadOnly || viewingHistoricalVersion}
            />
          </div>
        )}
        <MCMMappingsCard
          tenancyId={tenancyId}
          isReadOnly={isReadOnly}
          controlIds={controlIds}
          defaultEntityLink={defaultEntityLink}
          onEnsureControlLinked={addMcmControlToRisk}
        />
        <div style={{
          width: '100%',
          maxWidth: 1100,
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '1rem'
        }}>
          {!isReadOnly && (
            <button className="back-button" onClick={() => navigate('/risk-register')}>
              <i className="fa-solid fa-chevron-left"></i> Back to Risk Register
            </button>
          )}
        </div>
      </section>
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle>Delete Risk</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this risk? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The risk will not be recoverable.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Risk
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiskPage;
